import { FC } from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  PerformanceAgreementTrainingRequestsDocument,
  PerformanceAgreementTrainingsDocument,
  PerformanceProcessAgreementHeaderFragment,
  PerformanceProcessAgreementQuestionnairesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { usePrintableAgreement } from 'modules/performance_process/components/agreements/PrintableAgreement'

import { useActions } from './useActions'

export const ReviewProcessAgreementHeader: FC<{
  agreement: PerformanceProcessAgreementHeaderFragment
  isAgreementDocumentEnabled: boolean
}> = ({ agreement, isAgreementDocumentEnabled }) => {
  const { data, loading } = useQuery(PerformanceProcessAgreementQuestionnairesDocument, {
    variables: { id: agreement.id },
  })

  const { data: performanceAgreementTrainingsData, loading: loadingPerformanceAgreementTrainings } =
    useQuery(PerformanceAgreementTrainingsDocument, {
      variables: { accessId: agreement.target.access?.id },
    })

  const {
    data: performanceAgreementTrainingRequestsData,
    loading: loadingPerformanceAgreementTrainingRequests,
  } = useQuery(PerformanceAgreementTrainingRequestsDocument, {
    variables: { agreementId: agreement.id },
  })

  const isLoading =
    loading || loadingPerformanceAgreementTrainings || loadingPerformanceAgreementTrainingRequests

  const questionnaireAgreement = data?.performance?.agreement

  const isSigned = agreement.status === 'signed'
  const reviewer = agreement.managerEvaluation?.reviewerAccess
  const target = agreement.target.access

  const { pdf } = usePrintableAgreement(
    agreement,
    questionnaireAgreement,
    performanceAgreementTrainingRequestsData?.performance?.agreement,
    performanceAgreementTrainingsData?.trainings?.trainingsConnection?.nodes
  )

  const showDownloadButton = !isLoading && isSigned && pdf?.url

  const handleDownload = () => {
    pdf?.url && window.open(pdf.url, '_blank')
  }

  const { primaryAction, secondaryActions } = useActions(agreement)

  const downloadAction =
    showDownloadButton && !pdf?.loading
      ? {
          label: i18n.t('performance_process.agreements.actions.download'),
          icon: Download,
          onClick: handleDownload,
        }
      : undefined

  return (
    <ResourceHeader
      title={i18n.t('performance_process.agreements.page.title')}
      description={i18n.t('performance_process.agreements.page.description')}
      primaryAction={isAgreementDocumentEnabled ? primaryAction : downloadAction}
      secondaryActions={isAgreementDocumentEnabled ? secondaryActions : undefined}
      metadata={[
        {
          label: i18n.t('performance_process.reviews.common.evaluator'),
          value: {
            type: 'avatar',
            text: reviewer?.fullName || '',
            variant: {
              src: buildUrl(reviewer?.avatar),
              firstName: reviewer?.firstName || '',
              lastName: reviewer?.lastName || '',
              type: 'person',
            },
          },
        },
        {
          label: i18n.t('performance_process.reviews.common.evaluated'),
          value: {
            type: 'avatar',
            text: target?.fullName || '',
            variant: {
              src: buildUrl(target?.avatar),
              firstName: target?.firstName || '',
              lastName: target?.lastName || '',
              type: 'person',
            },
          },
        },
      ]}
    />
  )
}
